// One port, read against something (#294, ZENITH.md §8.3, §10.6): what the
// cell holds for it beside what Zenith would make of it — the stored
// intent, the plan on screen, or a past apply from the site's history — and,
// above that, what the port is doing right now (#367): its link, its power
// draw and the neighbour the switch reports, from the live read the page
// already made (§11.3, ADR-0045 decision 3). From here the port's intent is
// edited, described or removed, behind the same gate the table row offers
// the same acts on, through the same handlers.
//
// **What the cell holds comes from the plan and nowhere else, and it is not
// what the switch runs** (#573, ADR-0078 decision 1). `/v1` exposes no read of
// a switch's document; `:plan` reads the cell's copy, merges the intent and
// answers `changes[]`, both sides read by the parser the gate trusts. The
// column was headed Running and the plan filled it from the held document,
// while the switch ran something else. So
// the held column is filled while a plan is on screen and says so when
// none is, and the marks on the rows — which fields an apply would change —
// are the server's `differs`, never a comparison this page made. The only
// column this page fills itself is the stored intent's, which is the record
// the table already shows, and it is drawn beside the held values so the
// difference between "the intent says nothing" and "sent as the cell holds
// it" is visible: a PoE field the intent leaves blank is carried from the
// held document, and the plan column shows the value.
//
// **What the port is doing is the live read's and is never stored.** The
// block says when it was read and offers the read again; a field the switch
// did not answer says why, in the API's own reasons, and a neighbour matching
// no device of the site is shown as reported and made into nothing (decision 4).
//
// **A past apply is compared by generation, not by value.** The record an
// apply leaves holds the digest, the schema and `port_generations` — which
// generation of each port's intent it was built from — and neither the
// document nor the intent of the time. So the apply view says, per port,
// whether its intent has moved since, was added since or was removed since,
// and does not say what changed, because nothing stored can.
//
// Only the fields Zenith models appear, and the page says so under the
// table: a section the parser does not model is not here, and an absence here
// is not "nothing else changes". The whole document is the plan's request.
import { useState } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import { Icon, Info, Loading, Pill, ProblemBox, When } from "../../ui";
import { PortName, byNumber } from "./DeviceFace";
import { PortVlanNote, type PortVlanDisagreement } from "./switchVlans";
import type { useLiveState } from "./DeviceDetail";
import { LinkCell, NeighbourCell, ReportWhen, reasonWords, type ReportTimes } from "./Ports";
import { runsWords, setWords, type Space } from "../networks/vlanModel";

export type Change = components["schemas"]["SwitchPortChange"];
export type Fields = components["schemas"]["SwitchPortFields"];
type Field = components["schemas"]["SwitchPortField"];
type Intent = components["schemas"]["PortIntent"];
type Plan = components["schemas"]["SwitchPortPlan"];
type Apply = components["schemas"]["GeneratedConfig"];
type LivePort = components["schemas"]["SwitchPortState"];

/** The rows of the comparison, in the order the contract lists the fields. */
export const ROWS: { field: Field; label: string }[] = [
  { field: "enabled", label: "Enabled" },
  { field: "description", label: "Description" },
  { field: "untagged_vlan", label: "Untagged VLAN" },
  { field: "tagged_vlans", label: "Tagged VLANs" },
  { field: "poe.enabled", label: "PoE" },
  { field: "poe.priority", label: "PoE priority" },
  { field: "poe.power_limit_mw", label: "PoE power limit" },
  { field: "voice.mode", label: "Voice VLAN mode" },
  { field: "voice.priority", label: "Voice VLAN priority" },
  { field: "voice.detect_voice", label: "Voice detection" },
];

/** One field of one side, in words. `undefined` is a key the document does
 * not carry — the firmware's default, not false — and is drawn as such. With
 * the site's tag space, a tagged set reads by the customer's names (#590);
 * both sides read through the same function, so the words compare. */
export function word(field: Field, f: Fields | undefined, space?: Space): string | undefined {
  if (!f) return undefined;
  switch (field) {
    case "enabled": return f.enabled === undefined ? undefined : f.enabled ? "yes" : "no";
    case "description": return f.description || undefined;
    case "untagged_vlan": return f.untagged_vlan === undefined ? undefined : String(f.untagged_vlan);
    case "tagged_vlans": return f.tagged_vlans.length ? setWords(space, f.tagged_vlans) : "none";
    case "poe.enabled": return f.poe?.enabled === undefined ? undefined : f.poe.enabled ? "on" : "off";
    case "poe.priority": return f.poe?.priority || undefined;
    case "poe.power_limit_mw": return f.poe?.power_limit_mw === undefined ? undefined : `${f.poe.power_limit_mw / 1000} W`;
    case "voice.mode": return f.voice?.mode;
    case "voice.priority": return f.voice?.priority === undefined ? undefined : String(f.voice.priority);
    case "voice.detect_voice": return f.voice?.detect_voice;
  }
}

/** The stored intent in the shape the two document sides take, so one
 * formatter serves three columns. A PoE the intent leaves blank stays
 * absent, which the column then shows as "says nothing". */
export function fieldsOfIntent(p: Intent): Fields {
  return {
    enabled: p.enabled, description: p.description, untagged_vlan: p.untagged_vlan, tagged_vlans: p.tagged_vlans,
    ...(p.poe ? { poe: { enabled: p.poe.enabled, priority: p.poe.priority, power_limit_mw: p.poe.power_limit_mw } } : {}),
    // A port's voice intent (ADR-0369 decision 1a): only what an operator set,
    // so a field left to the controller reads as "says nothing".
    ...(p.voice?.intent ? { voice: { mode: p.voice.intent.mode, priority: p.voice.intent.priority, detect_voice: p.voice.intent.detect_voice } } : {}),
  };
}

/** What a past apply says of one port, from the generations it recorded
 * against the generation the intent is at now. */
export type Since = "unchanged" | "moved" | "added" | "removed" | "neither";
export function since(apply: Apply, port: string, now: number | undefined): { then?: number; now?: number; verdict: Since } {
  const then = apply.port_generations?.[port];
  if (then === undefined && now === undefined) return { verdict: "neither" };
  if (then === undefined) return { now, verdict: "added" };
  if (now === undefined) return { then, verdict: "removed" };
  return { then, now, verdict: then === now ? "unchanged" : "moved" };
}

type Against = "intent" | "plan" | "apply";

/** The intent acts the detail offers, when the caller may act (#367). The
 * handlers are the section's own — the same ones its table rows call — so
 * the row and the detail cannot disagree about what a port may be sent. */
export interface PortActs {
  mayConfigure: boolean;
  /** Which port the editor is open for, if any: "" for a port being named. */
  editing?: string;
  /** Whether this port's removal is awaiting confirmation. */
  removing: boolean;
  busy: boolean;
  onEdit(): void;
  onRemove(): void;
  onKeep(): void;
  onConfirmRemove(): void;
}

export default function PortDetail({ siteId, serial, port, intent, plan, generations, live, times, space, vlan, ownActs = true, ...acts }: PortActs & {
  /** Whether the detail draws the port's intent acts itself. Design's inspector
   * draws them at its head instead (#1891), where every edit of the port is. */
  ownActs?: boolean;
  siteId: string;
  /** The site's tag space, for a tagged set read by the customer's names. */
  space?: Space;
  serial: string;
  port: string;
  /** The stored intent for this port, when the site holds one. */
  intent?: Intent;
  /** The plan on screen, when there is one; its `changes` is the held side. */
  plan?: Plan;
  /** Every port's current intent generation, for the apply view's "since". */
  generations: Record<string, number>;
  /** The switch's live read, the page's own (#371): this shows one port of it. */
  live: ReturnType<typeof useLiveState>;
  /** When the report on screen was written and read (#466). */
  times: ReportTimes;
  /** Where this port's native VLAN, as the switch itself reports it, is not
   * the untagged VLAN the cell's document gives it (#574). Absent for every
   * port the switch does not fault, which draws nothing — the report cannot
   * see a tagged membership, so its silence is not a statement of agreement. */
  vlan?: PortVlanDisagreement;
}) {
  const [against, setAgainst] = useState<Against>(plan ? "plan" : "intent");
  const [applyId, setApplyId] = useState<string>();
  const change = plan?.changes?.find((c) => c.port === port);
  // The site's history is read when the operator asks for an apply and not
  // before: it is one more read on a page that has its budget (#277), for a
  // view most visits never open.
  const history = useQuery(() => against === "apply"
    ? api.GET("/sites/{id}/config/generated", { params: { path: { id: siteId } } })
    : SKIP, [against, siteId]);
  const applies = (history.data?.items ?? []).filter((a) => a.target_kind === "device" && a.target_id === serial);
  const apply = applies.find((a) => a.id === applyId) ?? applies[0];

  const pick = (a: Against) => (
    <button type="button" className={"btn btn-quiet" + (against === a ? " is-on" : "")} aria-pressed={against === a} onClick={() => setAgainst(a)}>
      {/* TODO(copy): "apply" stays a noun in this view (#1678). The glossary's verb form would be
          "an applied plan", and a record listed here can be one that never reached the controller
          (the Applied row below says so), so that wording would be false of it. */}
      {a === "intent" ? "Stored intent" : a === "plan" ? "Last plan" : "A past apply"}
    </button>
  );

  return (
    <div className="port-detail" role="group" aria-label={`${port} compared`}>
      <div className="page-head">
        <h3><PortName name={port} /></h3>
        {/* A plan is made where the editor is (#875): a device's page in
            Operate makes none, so it offers no plan to compare against. */}
        <div className="seg" role="group" aria-label="Compare against">{pick("intent")}{(plan || acts.mayConfigure) && pick("plan")}{pick("apply")}</div>
        {/* What the comparison reads and what an apply sends for this port, behind the ⓘ beside
            the choice it explains (#1890), each sentence whole. */}
        <Info label="About this comparison">
          {against === "apply"
            ? <>An apply&apos;s record keeps which generation of each port&apos;s planned settings it was built from. It keeps neither the configuration it sent nor the planned settings of the time. So this says whether the planned settings changed, not what changed.</>
            : (
              <>
                {!plan && (acts.mayConfigure
                  ? <>This comparison takes what the controller holds for this switch from a plan. Review &amp; apply makes a plan, which fills the held column and marks what applying it would change. </>
                  : <>This comparison takes what the controller holds for this switch from a plan. A plan is made on the Ports tab of Design › Wired, beside the editor. This page is read-only and makes none. </>)}
                {/* #369. */}
                {plan && change && !change.planned && <><code>{port}</code> has no planned settings, so applying the plan sends it exactly as the controller holds it. The held column is what is sent, whatever the switch runs for this port today. </>}
                {plan && change?.planned && change.differs.length === 0 && <>Applying the plan rewrites <code>{port}</code>&rsquo;s entry to what it already says. Nothing differs in the fields this build models. </>}
                {against === "intent" && intent && <>A field the planned settings leave blank is carried from the configuration the controller holds. The plan column shows the value, and the marks are the plan&apos;s. </>}
                Only the fields this build models are compared. A section this build does not model is not listed. That does not mean nothing else changes: the whole configuration is the plan&apos;s request, and the digest covers those bytes.
              </>
            )}
        </Info>
      </div>
      <PortNow port={port} live={live} times={times} />
      <PortVlanNote d={vlan} />
      {ownActs && <PortActions port={port} intent={intent} {...acts} />}
      {against !== "apply" && (
        <>
          <div className="table-wrap"><table className="compare" aria-label={`${port} held beside ${against === "plan" ? "the plan" : "the intent"}`}>
            <thead><tr><th>Field</th><th title="What the configuration the controller holds for this switch says, read by the plan. It is not read from the switch.">Held by the controller</th><th>{against === "plan" ? "After the plan is applied" : "Stored intent"}</th><th aria-label="Changes" /></tr></thead>
            <tbody>{ROWS.map(({ field, label }) => {
              const right = against === "plan" ? word(field, change?.planned, space) : intent ? word(field, fieldsOfIntent(intent), space) : undefined;
              const differs = change?.differs.includes(field) ?? false;
              // The numbers behind a named set, one hover away (#590).
              const numbers = (f: Fields | undefined) => (field === "tagged_vlans" && f?.tagged_vlans.length ? runsWords(f.tagged_vlans) : undefined);
              return (
                <tr key={field} className={differs ? "is-diff" : undefined}>
                  <th scope="row">{label}</th>
                  <td title={numbers(change?.held)}>{change ? (word(field, change.held, space) ?? <span className="muted">no key</span>) : <span className="muted">—</span>}</td>
                  <td title={numbers(against === "plan" ? change?.planned : intent ? fieldsOfIntent(intent) : undefined)}>{against === "plan"
                    ? change?.planned ? (right ?? <span className="muted">no key</span>) : change ? <span className="muted">sent as held</span> : <span className="muted">—</span>
                    : intent ? (right ?? <span className="muted">says nothing</span>) : <span className="muted">no intent</span>}</td>
                  <td>{differs && <Pill tone="warn" title="Applying the plan would change this field. The server compared the two configurations.">changes</Pill>}</td>
                </tr>
              );
            })}</tbody>
          </table></div>
          {plan && !change && <p className="hint">The switch&rsquo;s configuration declares no port <code>{port}</code>, so the plan has nothing to read for it.</p>}
        </>
      )}
      {against === "apply" && (
        <>
          {history.status === "loading" && <Loading label="Loading the site's applies" />}
          {history.status === "error" && <ProblemBox problem={history.problem} />}
          {history.status === "ready" && applies.length === 0 && <p className="muted">This switch has no apply in the site&apos;s history.</p>}
          {applies.length > 0 && apply && (
            <>
              <label className="hint">Apply{" "}
                <select aria-label="Apply" value={apply.id} onChange={(e) => setApplyId(e.target.value)}>
                  {applies.map((a) => <option key={a.id} value={a.id}>#{a.seq ?? "?"} · {new Date(a.validated_at).toLocaleString()} · {a.digest.slice(0, 12)}</option>)}
                </select>
              </label>
              <dl className="kv">
                <dt>Applied</dt><dd>{apply.applied_at ? <When value={apply.applied_at} /> : <span className="muted">never reached the controller</span>}</dd>
                <dt>Schema</dt><dd><code>{apply.schema_version}</code></dd>
                <dt>Digest</dt><dd><code>{apply.digest}</code></dd>
              </dl>
              <SinceWords port={port} apply={apply} now={generations[port]} />
              <MovedSince apply={apply} generations={generations} />
            </>
          )}
        </>
      )}
    </div>
  );
}

/** The port's power draw as the live read answered it: a figure, none, or
 * why it is not known. In watts, the unit the intent's limit is shown in. */
function PoeCell({ status, port }: { status: string; port?: LivePort }) {
  if (status === "loading") return <span className="muted">&hellip;</span>;
  if (!port) return <span className="muted">—</span>;
  const u = port.unanswered.find((x) => x.field === "poe");
  if (u) return <span className="muted" title={reasonWords(u.reason)}>unanswered · {u.reason}</span>;
  if (port.poe_draw_mw === undefined) return <span className="muted">—</span>;
  if (port.poe_draw_mw === 0) return <span>none drawn</span>;
  return <span>{(port.poe_draw_mw / 1000).toFixed(port.poe_draw_mw % 1000 === 0 ? 0 : 1)} W drawn</span>;
}

/** What the port was doing at the switch's last report (#367, #466): the
 * live read's answer for this one port, when the switch wrote the report and
 * when it was read, and the read again on request. The cells are the table's
 * own, so the two never say different things about one port. */
function PortNow({ port, live, times }: { port: string; live: ReturnType<typeof useLiveState>; times: ReportTimes }) {
  const set = live.status === "ready" ? live.data : undefined;
  const entry = set?.ports.find((p) => p.port === port);
  const status = live.status;
  return (
    <section className="port-now" aria-label={`${port} now`}>
      <div className="page-head"><span className="page-title"><h4>Now</h4><Info label="About an unknown device">An unknown device is a neighbor whose MAC matches no device of this site. It is shown as the switch reports it and is never made into a device, a claim or a link.</Info></span>
        <button type="button" className="btn btn-quiet btn-icon btn-sm" onClick={live.reload} disabled={status === "loading"}><Icon name="refresh" /><span>Read again</span></button>
      </div>
      {status === "error" && <p className="muted">Port state could not be read: {live.problem.message}</p>}
      {set?.unanswered && <p className="muted">Port state unanswered: {reasonWords(set.unanswered.reason)}.</p>}
      {set && !set.unanswered && !entry && <p className="muted">The switch&rsquo;s configuration declares no port <code>{port}</code>, so there is no live state to show for it.</p>}
      {(status === "loading" || entry) && (
        <dl className="kv">
          <dt>Link</dt><dd><LinkCell status={status} port={entry} /></dd>
          <dt>Power</dt><dd><PoeCell status={status} port={entry} /></dd>
          <dt>Neighbor</dt><dd><NeighbourCell status={status} port={entry} /></dd>
        </dl>
      )}
      {entry && entry.unanswered.length > 0 && (
        <p className="hint">Unanswered: {entry.unanswered.map((u, i) => <span key={u.field ?? i}>{i > 0 && "; "}<code>{u.field ?? "port"}</code> — {reasonWords(u.reason, u.field)}</span>)}.</p>
      )}
      <p className="hint">
        {/* ZENITH.md §11.3. */}
        {times.readAt ? <ReportWhen times={times} /> : <>Read from the switch&rsquo;s last report, never stored.</>}
      </p>
    </section>
  );
}

/** The intent acts, when the caller may act: edit or remove the intent the
 * port has, or describe a port that has none. The editor for another port
 * is said rather than closed under the operator: their edits are theirs. */
export function PortActions({ port, intent, mayConfigure, editing, removing, busy, onEdit, onRemove, onKeep, onConfirmRemove }: PortActs & { port: string; intent?: Intent }) {
  if (!mayConfigure) return null;
  if (editing === port) return <p className="hint port-acts">Editing <code>{port}</code>&rsquo;s planned settings below.</p>;
  const elsewhere = editing !== undefined;
  if (removing) {
    return (
      <p className="port-acts">
        <span className="muted">Remove <code>{port}</code>&rsquo;s planned settings? Applying a plan then sends this port as the controller holds it.</span>{" "}
        <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={onConfirmRemove}>Remove {port}&rsquo;s planned settings</button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={onKeep}>Keep</button>
      </p>
    );
  }
  return (
    <p className="port-acts">
      {intent
        ? (
          <>
            <button type="button" className="btn btn-icon btn-sm" disabled={elsewhere} onClick={onEdit}><Icon name="edit" /><span>Edit planned settings</span></button>
            <button type="button" className="btn btn-quiet btn-icon btn-sm" disabled={elsewhere} onClick={onRemove}><Icon name="delete" /><span>Remove planned settings</span></button>
          </>
        )
        : <button type="button" className="btn btn-icon btn-sm" disabled={elsewhere} onClick={onEdit}><Icon name="ports" /><span>Describe {port}</span></button>}
      {elsewhere && <span className="hint">The editor is open for {editing === "" ? "a port being named" : <code>{editing}</code>}. Save or cancel it first.</span>}
    </p>
  );
}

function SinceWords({ port, apply, now }: { port: string; apply: Apply; now?: number }) {
  const s = since(apply, port, now);
  const n = `#${apply.seq ?? "?"}`;
  switch (s.verdict) {
    case "unchanged": return <p><code>{port}</code>&rsquo;s planned settings are at generation {s.now}, the one apply {n} was built from. They have not changed since.</p>;
    case "moved": return <p>Apply {n} was built from <code>{port}</code>&rsquo;s planned settings at generation {s.then}. They are now at generation {s.now} — <Pill tone="warn">changed since</Pill></p>;
    case "added": return <p><code>{port}</code> had no planned settings when apply {n} was built. It has been given some since — <Pill tone="warn">added since</Pill></p>;
    case "removed": return <p>Apply {n} was built from <code>{port}</code>&rsquo;s planned settings at generation {s.then}. They have been removed since — <Pill tone="warn">removed since</Pill></p>;
    case "neither": return <p><code>{port}</code> had no planned settings when apply {n} was built and has none now.</p>;
  }
}

/** Every port whose intent is not where this apply left it, so the one
 * port's verdict is read beside the switch's. */
function MovedSince({ apply, generations }: { apply: Apply; generations: Record<string, number> }) {
  const names = [...new Set([...Object.keys(apply.port_generations ?? {}), ...Object.keys(generations)])].sort(byNumber);
  const moved = names.filter((p) => since(apply, p, generations[p]).verdict !== "unchanged" && since(apply, p, generations[p]).verdict !== "neither");
  return moved.length
    ? <p className="small">Since this apply, planned settings changed on {moved.map((p, i) => <span key={p}>{i > 0 && ", "}<code>{p}</code></span>)}.</p>
    : <p className="small muted">No port&apos;s planned settings have changed since this apply.</p>;
}
